import { useCallback, useRef, useState } from 'react';
import {
  LoginEventPopupFlow,
  USER_DATA_REFRESH_TIMEOUT_MS,
} from '@constants/common';
import { CustomEvents } from '@constants/events';
import { useDocumentEvent } from '@hooks/useDocumentEvent';
import { useUserData } from '@hooks/useUserData';
import { UserDataModel } from '@models/userData';
import { voidPromise, withTimeout } from '@utils/promise';
import { openHostLoginPopup } from '@utils/loginPopup';

type LoggedInAction = (userData: UserDataModel) => void;

type UseLoginThenOptions = {
  /** Login flow reported by the host analytics. */
  flow: LoginEventPopupFlow;
  /** Owner of the action, for error reports. */
  source: string;
};

/**
 * Runs an action for a logged-in user: straight away when the user is logged
 * in, after the login otherwise. A guest gets the host login popup; once the
 * login is through, userData is refreshed and the action gets the fresh copy.
 * Closing the login popup drops the action.
 *
 * Every "log in, then carry on" of the rescue offer goes through here, so moving
 * the login into React changes this hook only.
 */
export const useLoginThen = ({ flow, source }: UseLoginThenOptions) => {
  const { data: cachedUserData, refetch: refetchUserData } = useUserData();
  const pendingActionRef = useRef<LoggedInAction | null>(null);
  const [isWaitingForLogin, setIsWaitingForLogin] = useState(false);

  const fetchUserData = useCallback(async () => {
    const { data } = await withTimeout(
      refetchUserData({ cancelRefetch: false }),
      USER_DATA_REFRESH_TIMEOUT_MS,
      `${source}.refetchUserData`
    );

    return data?.data;
  }, [refetchUserData, source]);

  const waitForLogin = (action: LoggedInAction) => {
    pendingActionRef.current = action;
    setIsWaitingForLogin(true);
    openHostLoginPopup(flow);
  };

  const runLoggedIn = (action: LoggedInAction) => {
    if (cachedUserData?.userId) {
      action(cachedUserData);
      return;
    }

    // Not loaded yet (a page whose islands have not asked for it) or a guest:
    // the request settles which one.
    if (cachedUserData) {
      waitForLogin(action);
      return;
    }

    voidPromise(
      fetchUserData().then((userData) => {
        if (userData?.userId) {
          action(userData);
        } else {
          waitForLogin(action);
        }
      }),
      { source }
    );
  };

  const runPendingAction = async () => {
    const action = pendingActionRef.current;

    pendingActionRef.current = null;

    try {
      const userData = await fetchUserData();

      if (action && userData?.userId) {
        action(userData);
      }
    } finally {
      setIsWaitingForLogin(false);
    }
  };

  useDocumentEvent(
    CustomEvents.USER_AUTHORIZED_SUCCESS,
    () => voidPromise(runPendingAction(), { source }),
    { enabled: isWaitingForLogin }
  );

  // Fired only by the close button, never after a successful login.
  useDocumentEvent(
    CustomEvents.LOGIN_POPUP_CLOSE,
    () => {
      pendingActionRef.current = null;
      setIsWaitingForLogin(false);
    },
    { enabled: isWaitingForLogin }
  );

  return { runLoggedIn, isWaitingForLogin };
};
